import { Outlet, RouterProvider } from 'react-router';
import { type ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
// RUM のため react-router からではなく @datadog/browser-rum-react/react-router-v7 から import する
import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v7';
import { layout } from '../styled-system/recipes';
import { Blank } from './components/Blank';
import {
  integrationSignInCallbackLoader,
  IntegrationSignInCallbackPage,
} from './pages/Integration/IntegrationSignInCallback';
import { UnauthorizedPage } from './pages/Unauthorized';
import { GlobalHeader } from '#/components/GlobalHeader';
import { GlobalFooter } from '#/components/GlobalFooter';
import { MyPage } from '#/pages/MyPage';
import { EmailChangePage } from '#/pages/EmailChangePage';
// https://github.com/dw-zenid/zenid/issues/406
// 暫定対応のためZEN ID側のプライバシーポリシー画面はコメントアウト
// import { PrivacyPolicy } from '#/pages/PrivacyPolicy';
import { Terms } from '#/pages/Terms';
import { NoMatch } from '#/pages/NoMatch/NoMatch';
import { GenericError } from '#/components/Error/GenericError';
import { Login } from '#/pages/Login';
import {
  IntegrationFormPage,
  IntegrationVerifyPage,
  integrationVerifyLoader,
  integrationFormLoader,
} from '#/pages/Integration';
import { logoutLoader } from '#/pages/Logout';
import { CardLayout } from '#/components/Layout/CardLayout';
import { AuthProvider } from '#/utils/auth/AuthProvider';
import { auth0Client } from '#/utils/auth/auth0Client';

const FEATURE_FLAG_NNN_SSO = Boolean(import.meta.env.VITE_FEATURE_FLAG_NNN_SSO);

const { zenidContainer, mainContentsContainer, mainContentsInnerContainer, errorContainer } = layout();

function AppLayout({ children }: { children: ReactNode }) {
  return (
    <div
      className={zenidContainer}
      style={{ backgroundImage: `url(${import.meta.env.VITE_ASSETS_URL}/assets/images/zenid_bg.png)` }}
    >
      <GlobalHeader />
      <div className={mainContentsContainer}>
        <div className={mainContentsInnerContainer}>
          <ErrorBoundary
            fallback={
              <div className={errorContainer}>
                <GenericError />
              </div>
            }
          >
            {children}
          </ErrorBoundary>
        </div>
      </div>
      <GlobalFooter />
    </div>
  );
}

function Provider({ children }: { children: ReactNode }) {
  return <AuthProvider client={auth0Client}>{children}</AuthProvider>;
}

/**
 * アプリ全体のルーティング情報を管理する
 * TODO: AuthProvider によるリダイレクトを避けるためにルート定義が複雑になっているので、認証処理 (handleCallback / useLoginRequired) とリダイレクトを整理する
 */
const router = createBrowserRouter([
  FEATURE_FLAG_NNN_SSO
    ? {
        element: (
          <AppLayout>
            <CardLayout>
              <Outlet />
            </CardLayout>
          </AppLayout>
        ),
        children: [
          {
            path: 'integration/callback',
            element: <IntegrationSignInCallbackPage />,
            loader: integrationSignInCallbackLoader,
            HydrateFallback: Blank,
          },
        ],
      }
    : {},
  {
    path: '/',
    element: (
      <Provider>
        <AppLayout>
          <Outlet />
        </AppLayout>
      </Provider>
    ),
    children: [
      { path: 'login', element: <Login /> },
      { path: 'home', element: <MyPage /> },
      {
        path: 'logout',
        loader: logoutLoader,
        HydrateFallback: Blank,
      },
      {
        /**
         * Card レイアウトのページ郡
         */
        element: (
          <CardLayout>
            <Outlet />
          </CardLayout>
        ),
        children: [
          { path: 'edit/email', element: <EmailChangePage /> },
          FEATURE_FLAG_NNN_SSO
            ? {
                path: 'integration/form',
                element: <IntegrationFormPage />,
                loader: integrationFormLoader,
                hydrateFallbackElement: <div />,
              }
            : {},
          FEATURE_FLAG_NNN_SSO
            ? {
                path: 'integration/verify',
                element: <IntegrationVerifyPage />,
                loader: integrationVerifyLoader,
                HydrateFallback: Blank,
              }
            : {},
        ],
      },
      {
        path: 'unauthorized',
        element: <UnauthorizedPage />,
      },
      {
        path: 'terms',
        // https://github.com/dw-zenid/zenid/issues/406
        // 暫定対応のためZEN ID側のプライバシーポリシー画面はコメントアウト
        // { path: 'privacy', element: <PrivacyPolicy /> },
        children: [{ index: true, element: <Terms /> }],
      },
      { path: '*', element: <NoMatch /> },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
